import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import StorageOutlinedIcon from '@material-ui/icons/StorageOutlined';
import { RequestFailure } from '../../modules/shared/requestFailure';
import { PillTone } from './HealthPill';

export type TabErrorAction = 'retry' | 'catalog' | 'none';

export interface TabErrorShape {
  tone: Exclude<PillTone, 'success'>;
  title: string;
  detail: string;
  action: TabErrorAction;
  icon: ReactNode;
}

const ICON = { style: { fontSize: 21 } };

const SERVER_SIDE = new Set([500, 502, 503, 504]);

// Keyed on the code the backend already sends, and only then on the status. A
// retry is offered where retrying can actually change the answer.
export const tabErrorShape = (error: RequestFailure): TabErrorShape => {
  if (error.code === 'metrics_db_unreachable') {
    return {
      tone: 'warning',
      title: 'Metrics are not available yet',
      detail:
        "This squad's metrics database is not answering. A freshly created instance takes a few minutes to come up.",
      action: 'retry',
      icon: <StorageOutlinedIcon {...ICON} />,
    };
  }

  if (error.code === 'metrics_query_failed') {
    return {
      tone: 'warning',
      title: 'The metrics query failed',
      detail:
        'The metrics database answered with an error. Try again, and if it keeps failing, ask the platform team to check the backend logs.',
      action: 'retry',
      icon: <ErrorOutlineIcon {...ICON} />,
    };
  }

  if (
    error.code === 'identity_store_unreachable' ||
    error.code === 'gitops_failed'
  ) {
    return {
      tone: 'warning',
      title: 'This action could not be started',
      detail: 'The service behind it is not answering. Try again in a moment.',
      action: 'retry',
      icon: <ScheduleOutlinedIcon {...ICON} />,
    };
  }

  if (error.status === 404) {
    return {
      tone: 'danger',
      title: 'This instance is not available',
      detail:
        'It may have been decommissioned, or it may belong to another squad. Nothing is running here to show.',
      action: 'catalog',
      icon: <HighlightOffIcon {...ICON} />,
    };
  }

  if (error.status === 403) {
    return {
      tone: 'danger',
      title: 'You do not have access to this instance',
      detail: 'It belongs to a squad you are not a member of.',
      action: 'none',
      icon: <LockOutlinedIcon {...ICON} />,
    };
  }

  if (error.status === 400) {
    return {
      tone: 'warning',
      title: 'This tab could not read the instance',
      detail: 'The catalog entry may be incomplete.',
      action: 'retry',
      icon: <ErrorOutlineIcon {...ICON} />,
    };
  }

  if (error.status !== undefined && SERVER_SIDE.has(error.status)) {
    return {
      tone: 'warning',
      title: 'This instance is not answering yet',
      detail:
        'A freshly created instance takes a few minutes to come up. Try again shortly.',
      action: 'retry',
      icon: <ScheduleOutlinedIcon {...ICON} />,
    };
  }

  // No status at all means the request never reached a server.
  if (error.status === undefined) {
    return {
      tone: 'none',
      title: 'The portal could not reach the server',
      detail: 'Check your connection, then try again.',
      action: 'retry',
      icon: <CloudOffIcon {...ICON} />,
    };
  }

  return {
    tone: 'warning',
    title: 'This tab could not load',
    detail: 'Try again in a moment.',
    action: 'retry',
    icon: <ErrorOutlineIcon {...ICON} />,
  };
};

const BADGE_TONES: Record<TabErrorShape['tone'], string> = {
  warning: 'warning',
  danger: 'danger',
  neutral: 'neutral',
  none: 'none',
};

const badgeColors = (tone: TabErrorShape['tone']) =>
  tone === 'neutral' || tone === 'none'
    ? {
        color: 'var(--bui-fg-secondary)',
        background: 'var(--bui-bg-neutral-2)',
        borderColor: 'var(--bui-border-1)',
      }
    : {
        color: `var(--bui-fg-${BADGE_TONES[tone]})`,
        background: `var(--bui-bg-${BADGE_TONES[tone]})`,
        borderColor: `var(--bui-border-${BADGE_TONES[tone]})`,
      };

const button: React.CSSProperties = {
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  padding: '8px 18px',
  borderRadius: 'var(--bui-radius-2)',
  cursor: 'pointer',
  border: '1px solid var(--bui-bg-solid)',
  background: 'var(--bui-bg-solid)',
  color: 'var(--bui-fg-solid)',
};

const ghost: React.CSSProperties = {
  ...button,
  background: 'transparent',
  color: 'var(--bui-bg-solid)',
  textDecoration: 'none',
  display: 'inline-block',
};

export const TabError = ({
  error,
  onRetry,
}: {
  error: RequestFailure;
  onRetry?: () => void;
}) => {
  const shape = tabErrorShape(error);
  const colors = badgeColors(shape.tone);

  return (
    <div
      style={{
        border: '1px dashed var(--bui-border-1)',
        borderRadius: 'var(--bui-radius-3)',
        background: 'var(--bui-bg-neutral-1)',
        padding: '46px 24px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 10,
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid',
          marginBottom: 4,
          ...colors,
        }}
      >
        {shape.icon}
      </span>

      <strong style={{ fontSize: 17, fontWeight: 600 }}>{shape.title}</strong>

      <p
        style={{
          margin: 0,
          fontSize: 13.5,
          color: 'var(--bui-fg-secondary)',
          maxWidth: '48ch',
        }}
      >
        {shape.detail}
      </p>

      {shape.action === 'retry' && onRetry && (
        <button
          type="button"
          style={{ ...button, marginTop: 10 }}
          onClick={onRetry}
        >
          Try again
        </button>
      )}

      {shape.action === 'catalog' && (
        <RouterLink to="/catalog" style={{ ...ghost, marginTop: 10 }}>
          Back to catalog
        </RouterLink>
      )}

      {error.status !== undefined && (
        <details style={{ marginTop: 14 }}>
          <summary
            style={{
              fontSize: 12,
              color: 'var(--bui-fg-secondary)',
              cursor: 'pointer',
            }}
          >
            Technical detail
          </summary>
          <pre
            style={{
              margin: '9px 0 0',
              padding: '11px 13px',
              background: 'var(--bui-bg-neutral-2)',
              border: '1px solid var(--bui-border-1)',
              borderRadius: 'var(--bui-radius-2)',
              fontFamily: 'var(--bui-font-monospace)',
              fontSize: 11.5,
              color: 'var(--bui-fg-secondary)',
              textAlign: 'left',
              overflowX: 'auto',
            }}
          >
            {[error.status, error.code].filter(Boolean).join(' ')}
            {'\n'}
            {error.message}
          </pre>
        </details>
      )}
    </div>
  );
};
